import { UseEntityData } from "crqlar-react-common/dist/components/useEntity";
import { Alert, Autocomplete, Divider, Stack, Switch } from "@mui/material";
import { useMutation, useQuery } from "react-query";
import { useAllAgeCategories } from "crqlar-react-common/dist/utils/hooks/ageCategories/useAgeCategories";
import Typography from "@mui/material/Typography";
import Property from "crqlar-react-common/dist/components/Property";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import { Sector } from "crqlar-react-common/dist/models/sector/sector.model";
import { useAllSectors } from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {
  ApaleoConfig,
  ApaleoCredentials,
  ApaleoDataProcessingRules } from
"crqlar-react-common/dist/models/integrations/apaleo/apaleo.config";
import {
  FullSyncConfig,
  PollerConfig } from
"crqlar-react-common/dist/models/integrations/pollerConfig.model";
import { AgeCategory } from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import AdditionalProperties from "crqlar-react-common/dist/components/AdditionalProperties";
import { LoadingButton } from "@mui/lab";
import { MbscCalendarEvent } from "@mobiscroll/react";
import { ApaleoService } from "crqlar-react-common/dist/services/integrations/apaleo/apaleo.service";
import { useReadOnly } from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";
import { enqueueSnackbar } from "notistack";
import {isRequiredTextMissing} from "src/utils/requiredField.util";

interface Props {
  apaleoConfigData: UseEntityData<ApaleoConfig>;
}

const notify = (message: string, success: boolean) =>
enqueueSnackbar(message, {
  variant: success ? 'success' : 'error',
  anchorOrigin: { vertical: 'bottom', horizontal: 'center' }
});

const ApaleoConfigForm = ({ apaleoConfigData }: Props) => {

  const { t } = useTranslation();
  const sectors: Sector[] = useAllSectors();

  const readOnlyUser = useReadOnly();

  const apaleoConfig = apaleoConfigData.item;
  const nameMissing = isRequiredTextMissing(apaleoConfigData, apaleoConfig.name);
  const setApaleoConfig = apaleoConfigData.setItem;

  const ageCategories = useAllAgeCategories();

  const isDirty = apaleoConfigData.isDirty;

  //region Handlers

  const checkConnection = useMutation(
    ['checkApaleoConfig', apaleoConfig.credentials],
    async (event: MbscCalendarEvent) => {
      let success = false;
      try {
        if (apaleoConfig.credentials) {
          await ApaleoService.checkConnection(apaleoConfig);
          success = true;
        }
      } catch (error) {
      }

      notify(success ? t('Connection successful') : t('Connection failed'), success);
    }
  );

  const updateField = (field: keyof ApaleoConfig, value: any) => {
    setApaleoConfig({ ...apaleoConfig, [field]: value });
  };

  const updateCredentialsField = (field: keyof ApaleoCredentials, value: any) => {
    setApaleoConfig({
      ...apaleoConfig,
      credentials: {
        ...apaleoConfig.credentials,
        [field]: value
      }
    });
  };

  const updatePollerConfig = (field: keyof PollerConfig, value: any) => {
    setApaleoConfig({
      ...apaleoConfig,
      pollerConfig: {
        ...apaleoConfig.pollerConfig,
        [field]: value
      }
    });
  };

  const updateFullSyncConfig = (field: keyof FullSyncConfig, value: any) => {
    updatePollerConfig('fullSync', {
      ...apaleoConfig.pollerConfig?.fullSync,
      [field]: value
    });
  };

  const updateDataProcessingRules = (field: keyof ApaleoDataProcessingRules, value: any) => {
    updateField('dataProcessingRules', {
      ...apaleoConfig.dataProcessingRules,
      [field]: value
    });
  };

  const propertiesQuery = useQuery(
    ['apaleoProperties', apaleoConfig.credentials],
    async () => {
      if (!apaleoConfig.credentials?.clientId || !apaleoConfig.credentials?.clientSecret) {
        return [];
      }
      try {
        return await ApaleoService.getProperties(apaleoConfig);
      } catch (error) {
        return [];
      }
    },
    {
      enabled: !!(apaleoConfig.credentials?.clientId && apaleoConfig.credentials?.clientSecret)
    }
  );

  const properties = propertiesQuery.data || [];

  const webhooksQuery = useQuery(
    ['apaleoWebhooks', apaleoConfig.credentials],
    async () => {
      if (!apaleoConfig.credentials?.clientId || !apaleoConfig.credentials?.clientSecret) {
        return [];
      }
      try {
        return await ApaleoService.checkWebhooks(apaleoConfig);
      } catch (error) {
        return [];
      }
    },
    {
      enabled: !!(apaleoConfig.credentials?.clientId && apaleoConfig.credentials?.clientSecret)
    }
  );

  const setupWebhook = useMutation(
    async () => {
      return await ApaleoService.setupWebhook(apaleoConfig);
    },
    {
      onSuccess: () => {
        webhooksQuery.refetch();
        notify(t('Webhook setup successful'), true);
      },
      onError: () => {
        notify(t('Webhook setup failed'), false);
      }
    }
  );

  const removeWebhooks = useMutation(
    async () => {
      return await ApaleoService.removeAllWebhooks(apaleoConfig);
    },
    {
      onSuccess: () => {
        webhooksQuery.refetch();
        notify(t('Webhooks removed successfully'), true);
      },
      onError: () => {
        notify(t('Failed to remove webhooks'), false);
      }
    }
  );

  //endregion Handlers

  //region UI

  const credentialsSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Credentials')}
            </Typography>

            <Stack gap={1}>
                <Property
        title={t('Integration name')}
        tooltip={t('Internal label for identifying this apaleo integration configuration.')}>

                    <TextField
          id={'name'}
          size={'small'}
          value={apaleoConfig.name || ''}
          error={nameMissing}
          fullWidth
          onChange={(e) => updateField('name', e.target.value)} />

                </Property>

                <Property
        title={t('Client ID')}
        tooltip={t('apaleo client ID used to authorize API requests.')}>

                    <TextField
          id={'client-id'}
          size={'small'}
          value={apaleoConfig.credentials?.clientId || ''}
          fullWidth
          onChange={(e) => updateCredentialsField('clientId', e.target.value)} />

                </Property>

                <Property
        title={t('Client secret')}
        tooltip={t('apaleo client secret paired with the client ID.')}>

                    <TextField
          id={'client-secret'}
          size={'small'}
          value={apaleoConfig.credentials?.clientSecret || ''}
          fullWidth
          type={'password'}
          onChange={(e) => updateCredentialsField('clientSecret', e.target.value)} />

                </Property>

                <Stack direction={'row'} justifyContent={'flex-start'}>
                    <LoadingButton
          disabled={readOnlyUser}
          loading={checkConnection.isLoading}
          onClick={checkConnection.mutate}
          variant={'outlined'}>

                        {t('Test connection')}
                    </LoadingButton>
                </Stack>
            </Stack>
        </Stack>;


  const syncSetupAccordion =
  <AdditionalProperties title={t('Data sync setup')} variant={'outlined'}>

            <Typography textAlign={'start'} sx={{ mt: 1 }}>
                {t('Full sync')}
            </Typography>

            <Property
      title={t('Reservation days past')}
      tooltip={t('How far back full sync should look for reservations.')}>

                <TextField
        id={'reservation-days-past'}
        size={'small'}
        value={apaleoConfig.pollerConfig?.fullSync?.reservationDaysPast || ''}
        onChange={(e) => updateFullSyncConfig('reservationDaysPast', parseInt(e.target.value))}
        fullWidth
        type={'number'} />

            </Property>

            <Property
      title={t('Reservation days future')}
      tooltip={t('How far into the future full sync should look for reservations.')}>

                <TextField
        id={'reservation-days-future'}
        size={'small'}
        value={apaleoConfig.pollerConfig?.fullSync?.reservationDaysFuture || ''}
        onChange={(e) => updateFullSyncConfig('reservationDaysFuture', parseInt(e.target.value))}
        fullWidth
        type={'number'} />

            </Property>

            <Property
      title={t('Stale timeout minutes')}
      tooltip={t('Minutes before a full sync job is treated as stale.')}>

                <TextField
        id={'stale-timeout-minutes'}
        size={'small'}
        value={apaleoConfig.pollerConfig?.fullSync?.staleTimeoutMinutes || ''}
        onChange={(e) => updateFullSyncConfig('staleTimeoutMinutes', parseInt(e.target.value))}
        fullWidth
        type={'number'} />

            </Property>

        </AdditionalProperties>;


  const dataProcessingAccordion =
  <AdditionalProperties title={t('Data processing rules')} variant={'outlined'}>

            <Property
      title={t('Accommodation sector')}
      tooltip={t('Sector where imported accommodation reservations and resources should be mapped.')}>

                <Autocomplete
        id={'sectors'}
        options={sectors}
        getOptionLabel={(option: any) => option.name || ''}
        value={sectors.find((s) => s.id === apaleoConfig.dataProcessingRules?.accommodationSectorId) || null}
        renderInput={(params) =>
        <TextField
          {...params}
          fullWidth
          size={'small'} />

        }
        fullWidth
        onChange={(e, value) => {
          updateDataProcessingRules('accommodationSectorId', value?.id);
        }} />

            </Property>

            <Property
      title={t('Adults age category')}
      tooltip={t('Age category used for adult guest counts imported from apaleo.')}>

                <Autocomplete
        id={'adults-age-categories'}
        options={ageCategories}
        getOptionLabel={(option: AgeCategory) => option.name || ''}
        value={ageCategories.find((s) => s.id === apaleoConfig.dataProcessingRules?.adultsAgeCategoryId) || null}
        renderInput={(params) =>
        <TextField
          {...params}
          fullWidth
          size={'small'} />

        }
        fullWidth
        onChange={(e, value) => {
          updateDataProcessingRules('adultsAgeCategoryId', value?.id || undefined);
        }} />

            </Property>

            <Property
      title={t('Children age category')}
      tooltip={t('Age category used for child guest counts imported from apaleo.')}>

                <Autocomplete
        id={'children-age-categories'}
        options={ageCategories}
        getOptionLabel={(option: AgeCategory) => option.name || ''}
        value={ageCategories.find((s) => s.id === apaleoConfig.dataProcessingRules?.childrenAgeCategoryId) || null}
        renderInput={(params) =>
        <TextField
          {...params}
          fullWidth
          size={'small'} />

        }
        fullWidth
        onChange={(e, value) => {
          updateDataProcessingRules('childrenAgeCategoryId', value?.id || undefined);
        }} />

            </Property>

            <Property
      title={t('Teenager age category')}
      tooltip={t('Age category used for teenager guest counts imported from apaleo.')}>

                <Autocomplete
        id={'teenager-age-categories'}
        options={ageCategories}
        getOptionLabel={(option: AgeCategory) => option.name || ''}
        value={ageCategories.find((s) => s.id === apaleoConfig.dataProcessingRules?.teenagerAgeCategoryId) || null}
        renderInput={(params) =>
        <TextField
          {...params}
          fullWidth
          size={'small'} />

        }
        fullWidth
        onChange={(e, value) => {
          updateDataProcessingRules('teenagerAgeCategoryId', value?.id || undefined);
        }} />

            </Property>

            <Property
      title={t('Infant age category')}
      tooltip={t('Age category used for infant guest counts imported from apaleo.')}>

                <Autocomplete
        id={'infant-age-categories'}
        options={ageCategories}
        getOptionLabel={(option: AgeCategory) => option.name || ''}
        value={ageCategories.find((s) => s.id === apaleoConfig.dataProcessingRules?.infantAgeCategoryId) || null}
        renderInput={(params) =>
        <TextField
          {...params}
          fullWidth
          size={'small'} />

        }
        fullWidth
        onChange={(e, value) => {
          updateDataProcessingRules('infantAgeCategoryId', value?.id || undefined);
        }} />

            </Property>

            <Property
      title={t('Auto create resource for a room if not found')}
      tooltip={t('Creates missing room resources automatically when imported apaleo data references an unknown room.')}>

                <Switch
        id={'auto-create-resource-if-not-found'}
        checked={apaleoConfig.dataProcessingRules?.autoCreateResourceIfNotFound}
        onChange={(e) => updateDataProcessingRules('autoCreateResourceIfNotFound', e.target.checked)} />

            </Property>

            <Property
      title={t('Auto create service a room if not found')}
      tooltip={t('Creates missing services automatically when imported apaleo rate-plan data references an unknown service.')}>

                <Switch
        id={'auto-create-service-if-not-found'}
        checked={apaleoConfig.dataProcessingRules?.autoCreateServiceForRatePlanIfNotFound}
        onChange={(e) => updateDataProcessingRules('autoCreateServiceForRatePlanIfNotFound', e.target.checked)} />

            </Property>

        </AdditionalProperties>;


  const configurationSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Configuration')}
            </Typography>

            <Stack gap={1}>
                <Property
        title={t('Property ID / Name (optional, multiproperty)')}
        tooltip={t('Optional property identifier assigned to all data imported through this connector.')}>

                    <Stack
          width={'100%'}
          gap={1}>

                        <Alert
            variant={'outlined'}
            severity={'info'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            {t('Put property id or name here. If set, all incoming data via this connector (profiles, reservations...) will have this property id. You will be able to filter data by this property id in the future')}
                        </Alert>

                        <Alert
            variant={'outlined'}
            severity={'error'}
            sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                            {t('Do not change this value if you are not sure what you are doing! It may cause data inconsistency. If you want to change it, please first delete all data from this connector')}
                        </Alert>

                        <Autocomplete
            id={'property-id'}
            options={properties}
            getOptionLabel={(option) => {
              if (typeof option === 'string') {
                return option;
              }
              return option.name || option.code || option.id || '';
            }}
            // Changed to make sure that properties is the list here before invoking .find operation
            value={properties?.find((p) => p.id === apaleoConfig.propertyId || p.code === apaleoConfig.propertyId || p.name === apaleoConfig.propertyId) || apaleoConfig.propertyId || null}
            renderInput={(params) =>
            <TextField
              {...params}
              size={'small'}
              fullWidth
              required />

            }
            fullWidth
            onChange={(e, value) => {
              if (typeof value === 'string') {
                updateField('propertyId', value);
              } else {
                updateField('propertyId', value?.id || value?.code || '');
              }
            }}
            freeSolo />

                    </Stack>
                </Property>

                {syncSetupAccordion}
                {dataProcessingAccordion}
            </Stack>
        </Stack>;


  const webhooksSection =
  <Stack gap={2}>
            <Typography variant={'h5'} textAlign={'start'}>
                {t('Webhooks')}
            </Typography>

            <Stack gap={1}>
                {(() => {
        const webhooks = webhooksQuery.data || [];
        const hasWebhooks = webhooks.length > 0;
        const webhookForCurrentProperty = webhooks.find((webhook) =>
        apaleoConfig.propertyId && webhook.propertyIds?.includes(apaleoConfig.propertyId)
        );
        const webhookForDifferentProperty = webhooks.find((webhook) =>
        apaleoConfig.propertyId && !webhook.propertyIds?.includes(apaleoConfig.propertyId)
        );
        const hasCorrectWebhook = !!webhookForCurrentProperty;
        const hasWrongPropertyWebhook = hasWebhooks && webhookForDifferentProperty && apaleoConfig.propertyId;

        if (isDirty) {
          return (
            <Alert
              variant={'outlined'}
              severity={'warning'}
              sx={{ py: 0, lineHeight: 1, alignItems: 'center' }}>

                                {t('Please save the configuration before managing webhooks')}
                            </Alert>);

        }

        return (
          <Stack gap={1}>
                            {hasWebhooks &&
            <Property
              title={t('Webhook Status')}
              tooltip={t('Shows whether apaleo webhooks are registered for the configured property.')}>

                                    <Stack gap={1} width={'100%'} alignItems={'flex-start'}>
                                        {hasCorrectWebhook &&
                <Alert
                  variant={'outlined'}
                  severity={'success'}
                  sx={{ py: 0, lineHeight: 1, alignItems: 'center', width: '100%' }}>

                                                {t('Webhook is active for property', { propertyId: apaleoConfig.propertyId })}
                                            </Alert>
                }
                                        {hasWrongPropertyWebhook &&
                <Alert
                  variant={'outlined'}
                  severity={'warning'}
                  sx={{ py: 0, lineHeight: 1, alignItems: 'center', width: '100%' }}>

                                                {t('Webhook is active but for different property. Current webhooks are for properties',
                  { properties: webhooks.flatMap((w) => w.propertyIds || []).join(', ') })}
                                            </Alert>
                }
                                        <LoadingButton
                  loading={removeWebhooks.isLoading}
                  onClick={() => removeWebhooks.mutate()}
                  variant="outlined"
                  color="error"
                  size="small">

                                            {t('Remove All Webhooks')}
                                        </LoadingButton>
                                    </Stack>
                                </Property>
            }

                            {!hasWebhooks &&
            <Property
              title={t('Setup Webhook')}
              tooltip={t('Registers apaleo webhooks so reservation updates can sync in near real time.')}>

                                    <Stack gap={1} width={'100%'} alignItems={'flex-start'}>
                                        <Alert
                  variant={'outlined'}
                  severity={'info'}
                  sx={{ py: 0, lineHeight: 1, alignItems: 'center', width: '100%' }}>

                                            {t('Setup webhook to receive real-time updates for property',
                  { propertyId: apaleoConfig.propertyId || 'All properties' })}
                                        </Alert>
                                        <LoadingButton
                  loading={setupWebhook.isLoading}
                  onClick={() => setupWebhook.mutate()}
                  variant="contained"
                  size="small">

                                            {t('Setup Webhook')}
                                        </LoadingButton>
                                    </Stack>
                                </Property>
            }

                            <Stack gap={1}>
                                {
              webhooks.map((webhook, index) =>
              <Stack key={index}>
                                            <Typography>
                                                <strong>{t("Webhook")} {index + 1}</strong>
                                            </Typography>
                                            <Typography>
                                                {t("Property:")} {webhook.propertyIds?.join(', ')}
                                            </Typography>
                                            <Typography>
                                                {t("Events:")} {webhook.events?.join(', ')}
                                            </Typography>
                                            <Typography>
                                                {t("Endpoint URL:")} {webhook.endpointUrl}
                                            </Typography>
                                        </Stack>
              )
              }
                            </Stack>
                        </Stack>);

      })()}
            </Stack>
        </Stack>;


  //endregion UI

  return (
    <Stack
      sx={{
        width: '100%',
        gap: 6
      }}>

            {credentialsSection}
            <Divider />
            {configurationSection}
            <Divider />
            {webhooksSection}
        </Stack>);


};

export default ApaleoConfigForm;
